import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Sparkles, MapPin, ArrowRight, Compass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Popular quick-start prompts
const QUICK_PROMPTS = [
  'anxiety',
  'grief',
  'tightness in my chest',
  'feeling disconnected',
  'trouble sleeping',
  'chronic stress',
  'feeling stuck',
  'low energy',
];

const STRENGTH_COLORS = {
  High: 'bg-amber-100 text-amber-800 border-amber-300',
  Strong: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  Moderate: 'bg-stone-100 text-stone-700 border-stone-300',
};

const ConcernSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [concerns, setConcerns] = useState([]); // catalog for autocomplete
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searched, setSearched] = useState('');
  const inputWrapRef = useRef(null);

  // Load concern catalog for autocomplete
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/concerns`);
        setConcerns(res.data || []);
      } catch (err) {
        console.error('Failed to load concerns', err);
      }
    };
    load();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (inputWrapRef.current && !inputWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Compute autocomplete suggestions from the concern catalog
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set();
    const out = [];
    // Match concern names first
    for (const c of concerns) {
      if (c.concern?.toLowerCase().includes(q) && !seen.has(c.concern.toLowerCase())) {
        out.push({ label: c.concern, kind: 'concern' });
        seen.add(c.concern.toLowerCase());
      }
      // Then matching keywords (surface the concern they belong to)
      for (const kw of c.keywords || []) {
        if (kw?.toLowerCase().includes(q) && !seen.has(kw.toLowerCase())) {
          out.push({ label: kw, kind: 'keyword', concern: c.concern });
          seen.add(kw.toLowerCase());
        }
        if (out.length >= 8) break;
      }
      if (out.length >= 8) break;
    }
    return out.slice(0, 8);
  }, [query, concerns]);

  const runSearch = async (text) => {
    const q = (text ?? query).trim();
    if (!q) return;
    setLoading(true);
    setShowSuggestions(false);
    setSearched(q);
    try {
      const res = await axios.get(`${API}/concerns/search`, { params: { q } });
      setResults(res.data);
    } catch (err) {
      console.error(err);
      setResults({ query: q, matched_concerns: [], modalities: [], healers: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch();
    }
  };

  const applySuggestion = (s) => {
    setQuery(s.label);
    setShowSuggestions(false);
    runSearch(s.label);
  };

  const initialsBadge = (name) => (name || '?').split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

  return (
    <section id="concern-search" className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-stone-50 to-amber-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 mb-4">
            <Compass className="w-4 h-4 text-amber-700" />
            <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">Find Your Healing Path</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" style={{ color: '#3E2723' }}>
            What are you experiencing?
          </h2>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto">
            Type what you're going through in your own words. We'll instantly match you to healing approaches and Ourmend healers who may help.
          </p>
        </div>

        {/* Search input */}
        <div className="relative" ref={inputWrapRef}>
          <div className="flex gap-2 sm:gap-3 bg-white shadow-lg rounded-2xl p-2 sm:p-3 border border-stone-200">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., anxiety, tightness in my chest, feeling stuck..."
                className="pl-12 h-12 sm:h-14 text-base border-0 focus-visible:ring-0 shadow-none bg-white"
                data-testid="concern-search-input"
              />
            </div>
            <Button
              onClick={() => runSearch()}
              disabled={loading || !query.trim()}
              className="h-12 sm:h-14 px-5 sm:px-7 bg-[#8B4513] hover:bg-[#6B3410] text-white font-medium"
              data-testid="concern-search-btn"
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {/* Autocomplete dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-20 mt-2 left-0 right-0 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden" data-testid="concern-autocomplete">
              {suggestions.map((s, i) => (
                <button
                  key={`${s.kind}-${s.label}-${i}`}
                  type="button"
                  onClick={() => applySuggestion(s)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-amber-50 transition-colors border-b border-stone-100 last:border-b-0"
                  data-testid={`concern-suggestion-${i}`}
                >
                  <Search className="w-4 h-4 text-stone-400 flex-shrink-0" />
                  <span className="text-sm text-stone-800 flex-1">{s.label}</span>
                  {s.kind === 'keyword' && (
                    <span className="text-xs text-stone-400">→ {s.concern}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick prompts */}
        {!results && (
          <div className="mt-5 flex flex-wrap gap-2 justify-center" data-testid="concern-quick-prompts">
            <span className="text-xs text-stone-500 self-center mr-1">Try:</span>
            {QUICK_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setQuery(p); runSearch(p); }}
                className="text-xs px-3 py-1.5 rounded-full bg-white border border-stone-300 text-stone-700 hover:border-[#8B4513] hover:text-[#8B4513] transition-colors"
                data-testid={`concern-prompt-${p.replace(/\s+/g, '-')}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="mt-8 space-y-6 fade-in" data-testid="concern-results">
            {results.modalities.length === 0 ? (
              <Card className="p-8 text-center bg-white">
                <p className="text-stone-700 mb-2">
                  We couldn't find a strong match for <span className="font-semibold">"{searched}"</span>.
                </p>
                <p className="text-sm text-stone-500 mb-4">
                  Try a broader term (like "stress", "grief", or "pain") or browse all healers directly.
                </p>
                <Button onClick={() => navigate('/browse')} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="concern-browse-all-btn">
                  Browse All Healers
                </Button>
              </Card>
            ) : (
              <>
                {/* Disclaimer */}
                <Card className="p-4 bg-amber-50/60 border-amber-200">
                  <p className="text-sm text-stone-700">
                    <Sparkles className="w-4 h-4 inline text-amber-700 mr-1.5 -mt-0.5" />
                    <strong>Based on what you shared, these healing approaches may be worth exploring.</strong>
                    <span className="text-stone-600"> This is a wellness discovery tool — not a diagnosis. Always consult a licensed provider for medical or mental-health concerns.</span>
                  </p>
                </Card>

                {/* Recommended modalities */}
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: '#3E2723' }}>
                    Recommended Healing Approaches
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="concern-modalities-list">
                    {results.modalities.map((m, idx) => (
                      <Card key={m.modality} className="p-5 bg-white hover:shadow-md transition-shadow" data-testid={`concern-modality-${idx}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-bold text-stone-900">{m.modality}</h4>
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${STRENGTH_COLORS[m.match_strength] || STRENGTH_COLORS.Moderate}`}>
                            {m.match_strength} match
                          </span>
                        </div>
                        <p className="text-sm text-stone-600 leading-relaxed">{m.reason}</p>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Matching healers */}
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: '#3E2723' }}>
                    Ourmend Healers Who May Be a Good Fit
                  </h3>
                  {results.healers.length === 0 ? (
                    <Card className="p-6 bg-white text-center">
                      <p className="text-stone-700 mb-1">Our healer community is still growing in these modalities.</p>
                      <p className="text-sm text-stone-500 mb-4">Check back soon or browse all healers to see what's available today.</p>
                      <Button onClick={() => navigate('/browse')} variant="outline" data-testid="concern-browse-outline-btn">
                        Browse All Healers <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Card>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="concern-healers-list">
                      {results.healers.slice(0, 6).map((h, idx) => (
                        <button
                          key={h.user_id}
                          type="button"
                          onClick={() => navigate(`/healer/${h.user_id}`)}
                          className="text-left"
                          data-testid={`concern-healer-${idx}`}
                        >
                          <Card className="p-4 h-full bg-white hover:shadow-lg transition-shadow border-stone-200">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center text-white font-bold">
                                {h.profile_image ? (
                                  <img
                                    src={h.profile_image.startsWith('http') ? h.profile_image : `${API}/files/${h.profile_image}`}
                                    alt={h.user?.name || 'Healer'}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  initialsBadge(h.user?.name)
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-stone-900 truncate">{h.user?.name || 'Healer'}</p>
                                <Badge variant="outline" className="mt-1 text-[11px]">{h.healer_type}</Badge>
                                {h.location && (
                                  <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {h.location}
                                  </p>
                                )}
                              </div>
                            </div>
                          </Card>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => { setResults(null); setQuery(''); }}
                    className="text-sm text-[#8B4513] font-medium hover:underline"
                    data-testid="concern-reset-btn"
                  >
                    ← Start a new search
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ConcernSearch;
